import { useCallback, useEffect } from "react";
import { useRouter } from "next/compat/router";
import { useSwrGet } from "@fwa/src/hooks/useSwrGet";
import { Box, Pagination, Text } from "@cruk/cruk-react-components";

import { fwsUrlTeamMembers } from "@fwa/src/services/teamService";
import { numberWithCommas } from "@fwa/src/utils/formatUtils";
import { useTracking } from "@fwa/src/hooks/useTracking";

import { LeaderboardList } from "@fwa/src/components/LeaderboardList";

import {
  type AllTeamsPageType,
  type FundraiserType,
  type FundraisingPageType,
  type FwsResponseData,
  type TeamMemberType,
  type TeamPageType,
} from "@fwa/src/types";
import {
  fwsUrSuperTeamPageFundraisingPages,
  fwsUrSuperTeamTeamPages,
} from "@fwa/src/services/superTeamService";
import { useFundraiserContext } from "@fwa/src/contexts/FundraiserContext";
import { queryAsString } from "@fwa/src/utils/urlUtils";

const ITEMS_PER_PAGE = 10;

type LeaderboardMembershipsUrlProps = {
  memberType?: "page" | "team";
  page: AllTeamsPageType;
  currentPage: number;
  fundraiser?: FundraiserType | null;
};

// do we want membership for team or superteam and do want member pages or member teams
const leaderboardMembershipsUrl = ({
  memberType = "page",
  page,
  currentPage,
  fundraiser,
}: LeaderboardMembershipsUrlProps) => {
  const { entityType } = page;

  const teamMembersUrl = page
    ? fwsUrlTeamMembers({
        teamId: page.uniqueId || "",
        currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
        sort: "amount",
        loggedInFundraiserId: fundraiser?.uniqueId,
      })
    : null;

  const superTeamMemberPagesUrl = page
    ? fwsUrSuperTeamPageFundraisingPages({
        teamId: page.uniqueId,
        currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
        sort: "amount",
      })
    : null;

  const superTeamMemberTeamsUrl = page
    ? fwsUrSuperTeamTeamPages({
        teamId: page.uniqueId || "",
        currentPage,
        itemsPerPage: ITEMS_PER_PAGE,
        sort: "amount",
      })
    : null;

  const membersUrl =
    entityType === "Team"
      ? teamMembersUrl
      : memberType === "page"
        ? superTeamMemberPagesUrl
        : superTeamMemberTeamsUrl;

  return membersUrl;
};

// TeamLeaderboardListPaginated handles page data and pagination state
// TeamLeaderboardList handles membership data for current page
export const TeamLeaderboardListPaginated = ({
  page,
  memberType,
}: {
  page: AllTeamsPageType;
  memberType: "page" | "team";
}) => {
  const router = useRouter();
  const { page: pageNumberInQueryString } = router?.query || {};
  const { entityType } = page;
  const { trackEventGtm, trackError } = useTracking();
  const currentPage = parseInt(
    queryAsString(pageNumberInQueryString || "1"),
    10,
  );
  const startItemIndex = ITEMS_PER_PAGE * Math.max(currentPage - 1, 0);
  const [fundraiserContext] = useFundraiserContext();
  const { fundraiser, loggedInStatus } = fundraiserContext;

  const url = leaderboardMembershipsUrl({
    memberType,
    page,
    fundraiser,
    currentPage,
  });

  const { data, error, totalItems } =
    useSwrGet<
      FwsResponseData<TeamMemberType | FundraisingPageType | TeamPageType>
    >(url);

  const itemPage = data?.metadata?.itemPage || 0;
  const results = data?.results;
  // Team members use members are returned as TeamMemberships
  // SuperTeams get member teams and pages with dedicated endpoints
  const members: FundraisingPageType[] | TeamPageType[] = results
    ? entityType === "Team"
      ? results?.map((member) => {
          const teamMember = member as TeamMemberType;
          return teamMember.childPage;
        })
      : (results as FundraisingPageType[] | TeamPageType[])
    : [];

  const showingText = totalItems
    ? `Showing ${startItemIndex + 1} to ${
        startItemIndex + (results?.length || 0)
      } of ${numberWithCommas(totalItems)} members`
    : "0 results";

  // We are effectively storing page number in the query string
  const setPage = useCallback(
    (pageNumber: number) => {
      router
        ?.push(
          {
            pathname: router.pathname,
            query: { ...router.query, page: pageNumber },
          },
          undefined,
          { shallow: true },
        )
        .catch((err) => {
          trackError(err as Error, {
            component: "LeaderboardListPaginated",
            function: "setPage",
          });
        });
    },
    [router, trackError],
  );

  // once logged in & first request for members has been run & we receive page number in the metadata of request
  // set the page number in the query string so everything runs again.
  useEffect(() => {
    if (loggedInStatus === "loggedIn" && !pageNumberInQueryString && itemPage) {
      setPage(itemPage);
    }
  }, [itemPage, pageNumberInQueryString, loggedInStatus, setPage]);

  return (
    <Box backgroundColor="background-light" padding="none" margin="none">
      {data ? (
        <Text role="alert" textAlign="center">
          {showingText}
        </Text>
      ) : null}
      <LeaderboardList
        memberType={memberType}
        pages={results ? members : null}
        error={error}
        currentPage={currentPage}
        itemsPerPage={ITEMS_PER_PAGE}
      />
      <Pagination
        current={currentPage}
        perPage={ITEMS_PER_PAGE}
        items={totalItems || members.length}
        pagerCallback={(n: number) => {
          trackEventGtm({
            event: "leaderboardlist change",
            currentPage: n,
          });
          setPage(n);
        }}
      />
    </Box>
  );
};

export default TeamLeaderboardListPaginated;
